/*
 * IconListGridItem  component
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback } from "react";
import { BEM, IIconPackageURLMapping } from "@adapptio/lib-components";
import { isFunction } from "@adapptio/lib-shared";
import { Icon, ICON_SIZE } from "../Icon";
import { useIconListContext } from "./IconListContext";

export interface IIconListPreviewProps<Name extends string = string> {
	className?: string;
	packName?: string;
	iconName?: Name;
	title?: string;
	active?: boolean;
	size?: ICON_SIZE;
	urlMapping?: IIconPackageURLMapping;
	onClick?: (packName: string, iconName: Name) => void;
	skeleton?: boolean;
}

export const IconListPreview: React.FC<IIconListPreviewProps> = ({
	className,
	packName,
	iconName,
	size,
	title,
	active,
	onClick,
	urlMapping,
	skeleton
}) => {
	const context = useIconListContext();
	const iconSize = size || context?.iconPreviewSize;
	const baseClass = BEM("edt-icon-list-preview", null, { active, size: iconSize });

	const handleOnClick = useCallback(() => {
		if (isFunction(onClick)) {
			return onClick(packName, iconName);
		}

		if (isFunction(context?.onIconSelect)) {
			return context.onIconSelect(packName, iconName);
		}
	}, [ packName, iconName, onClick ]);

	if (className) {
		baseClass.add(className);
	}

	return (
		<div className={baseClass.toClassName()} title={title} onClick={handleOnClick}>
			{skeleton ? (
				<div className={baseClass.element("skeleton").toClassName()} />
			) : (
				<Icon name={iconName} size={iconSize} urlMapping={urlMapping || context?.urlMapping} />
			)}
		</div>
	);
};
